import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Lock, ArrowRight, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing or invalid reset link.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match.");
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — you're signed in.");
      if (refresh) await refresh();
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <Header />
      <div className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <Link to="/login" data-testid="reset-back-to-login" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0F172A] mb-6">
            <ArrowLeft className="h-4 w-4"/> Back to sign in
          </Link>
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Choose a new password</h1>
            <p className="text-sm text-[#475569] mt-2">At least 8 characters.</p>
          </div>
          <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm space-y-5" data-testid="reset-form">
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">New password</span>
              <div className="mt-1.5 relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
              </div>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Confirm new password</span>
              <div className="mt-1.5 relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
              </div>
            </label>
            <button type="submit" disabled={submitting || !token} data-testid="reset-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F26953] text-white font-semibold py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60 transition-colors">
              {submitting ? "Updating…" : <>Update password <ArrowRight className="h-4 w-4"/></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
